<template>
  <div ref="root" class="language-picker">
    <button
      class="language-trigger"
      type="button"
      :aria-label="t('app.common.language')"
      aria-haspopup="menu"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="language-icon"><UIcon name="i-lucide-languages" /></span>
      <span class="language-current"
        ><strong>{{ current.label }}</strong
        ><small>{{ current.short }}</small></span
      >
      <UIcon class="language-chevron" name="i-lucide-chevron-down" />
    </button>
    <Transition name="language-menu">
      <div v-if="open" class="language-options" role="menu" :aria-label="t('app.common.language')">
        <button
          v-for="item in localeOptions"
          :key="item.code"
          type="button"
          role="menuitem"
          :lang="item.code"
          :class="{ active: item.code === appStore.locale }"
          @click="change(item.code)"
        >
          <span class="language-code">{{ item.short }}</span>
          <span>{{ item.label }}</span>
          <UIcon
            :name="item.code === appStore.locale ? 'i-lucide-check' : 'i-lucide-chevron-right'"
          />
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { localeOptions, localeToRouteSegment, type LocaleCode } from '@/i18n'
import { useAppStore } from '@/stores/app'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const root = ref<HTMLElement>()
const open = ref(false)
const current = computed(
  () => localeOptions.find((item) => item.code === appStore.locale) || localeOptions[0],
)
const change = (locale: LocaleCode) => {
  open.value = false
  if (locale === appStore.locale) return

  appStore.setLocale(locale)
  const segment = localeToRouteSegment(locale)
  const { lang: _lang, ...query } = route.query
  let target: RouteLocationRaw
  if (route.name) {
    target = {
      name: route.name,
      params: { ...route.params, locale: segment },
      query,
      hash: route.hash,
    }
  } else {
    const path = route.path.replace(/^\/(?:zh|zh-cn|en|ja|ko|fr|de|ru|ar)(?=\/|$)/i, '') || '/'
    target = { path: `/${segment}${path === '/' ? '' : path}`, query, hash: route.hash }
  }
  window.location.replace(router.resolve(target).href)
}
onClickOutside(root, () => {
  open.value = false
})
</script>

<style scoped>
.language-picker {
  position: relative;
  flex: none;
}
.language-trigger {
  min-width: 126px;
  height: 42px;
  padding: 0 10px 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--fb-line);
  border-radius: 12px;
  outline: none;
  background: color-mix(in srgb, var(--fb-surface) 92%, transparent);
  color: var(--fb-ink);
  box-shadow: 0 4px 14px rgba(10, 37, 51, 0.04);
  cursor: pointer;
  transition:
    border-color 0.18s,
    box-shadow 0.18s,
    transform 0.18s;
}
.language-trigger:hover,
.language-trigger:focus-visible {
  border-color: color-mix(in srgb, var(--fb-coral) 48%, var(--fb-line));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fb-coral) 10%, transparent);
}
.language-icon {
  flex: none;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  background: var(--fb-coral-soft);
  color: var(--fb-coral-dark);
}
.language-current {
  min-width: 0;
  display: grid;
  flex: 1;
  text-align: start;
  line-height: 1.05;
}
.language-current strong {
  overflow: hidden;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.language-current small {
  margin-top: 3px;
  color: var(--fb-ink-soft);
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.1em;
}
.language-chevron {
  flex: none;
  color: var(--fb-ink-soft);
  transition: transform 0.18s;
}
.language-trigger[aria-expanded='true'] .language-chevron {
  transform: rotate(180deg);
}
.language-options {
  position: absolute;
  inset-inline-end: 0;
  top: calc(100% + 8px);
  z-index: 100;
  width: 210px;
  max-height: min(430px, 70vh);
  padding: 7px;
  overflow-y: auto;
  border: 1px solid var(--fb-line);
  border-radius: 14px;
  background: var(--fb-surface-raised);
  box-shadow: var(--fb-shadow);
}
.language-options button {
  width: 100%;
  min-height: 42px;
  padding: 0 9px;
  display: grid;
  grid-template-columns: 34px 1fr auto;
  align-items: center;
  gap: 9px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--fb-ink);
  text-align: start;
  cursor: pointer;
}
.language-options button:hover {
  background: var(--fb-paper);
}
.language-options button.active {
  background: var(--fb-coral-soft);
  color: var(--fb-coral-dark);
  font-weight: 750;
}
.language-code {
  width: 32px;
  height: 25px;
  display: grid;
  place-items: center;
  border: 1px solid var(--fb-line);
  border-radius: 7px;
  color: var(--fb-ink-soft);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.08em;
}
.language-options button.active .language-code {
  border-color: color-mix(in srgb, var(--fb-coral) 40%, var(--fb-line));
  color: var(--fb-coral-dark);
}
.language-menu-enter-active,
.language-menu-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
  transform-origin: top right;
}
.language-menu-enter-from,
.language-menu-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.98);
}
@media (max-width: 520px) {
  .language-trigger {
    min-width: 112px;
  }
  .language-current small {
    display: none;
  }
}
</style>
